import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { ReactComponent as DarkModeLogo } from 'assets/logos/logo.svg';
import { ReactComponent as LightModeLogo } from 'assets/logos/symboticware_logo.svg';
import yokohamaLoaderLogoDark from 'assets/logos/yokohama_logo.png';
import yokohamaLoaderLogoLight from 'assets/logos/yokohama_logo_light.png';
import './LoadingScreen.css';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import {
  CLIENT_THEME,
  clientThemePalette,
  getClientLogoSmall,
} from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

interface LoadingScreenProps {
  progress?: number;
  isFullScreen?: boolean;
  message?: string;
}

export const SingleFadeLoader: React.FC<LoadingScreenProps> = ({
  progress,
  isFullScreen = true,
  message,
}) => {
  const { t } = useTranslation();
  const resolvedMessage = message ?? t('cdp_loading');
  const { enableDarkTheme } = useAppTheme();
  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const clientLogo =
    CLIENT_THEME === 'yokohama'
      ? {
          type: 'img' as const,
          src: enableDarkTheme
            ? yokohamaLoaderLogoLight
            : yokohamaLoaderLogoDark,
          alt: t('yokohama'),
        }
      : getClientLogoSmall();

  const renderLogo = () => {
    if (clientLogo.type === 'img') {
      return (
        <div
          style={{
            position: 'relative',
            width: 120,
            height: 120,
            flexShrink: 0,
          }}
        >
          {/* Spinning ring */}
          <svg
            viewBox="0 0 120 120"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              animation: 'spin-ring 1.4s linear infinite',
            }}
          >
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke={themeColors.primary}
              strokeWidth="4"
              strokeDasharray="260"
              strokeDashoffset="200"
              strokeLinecap="round"
            />
          </svg>
          {/* Logo centered inside ring */}
          <img
            src={clientLogo.src}
            alt={clientLogo.alt}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '70px',
              height: '70px',
              objectFit: 'contain',
            }}
          />
        </div>
      );
    }
    return enableDarkTheme ? (
      <DarkModeLogo className="loading-icon" />
    ) : (
      <LightModeLogo className="loading-icon" />
    );
  };

  return (
    <div
      className={isFullScreen ? 'loading-screen' : 'loading-screen-small'}
      style={
        isFullScreen ? { backgroundColor: themeColors.navbarBg } : undefined
      }
    >
      {renderLogo()}
      <Typography
        className="loading-text"
        sx={{
          color: enableDarkTheme
            ? ThemePalette.typography.white
            : ThemePalette.typography.darkGrey,
        }}
      >
        {resolvedMessage}
      </Typography>
      {progress !== undefined && (
        <Box sx={{ width: '50%', maxWidth: '100px', margin: '0 auto' }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: '10px',
              borderRadius: '5px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: themeColors.primary,
              },
            }}
          />
        </Box>
      )}
    </div>
  );
};
